import React, { useEffect, useRef } from 'react';
import { Sparkles } from 'lucide-react';
import { MentionFriend } from '../hooks/useMention';
import AvatarRenderer from './AvatarRenderer';

interface MentionDropdownProps {
    results: MentionFriend[];
    selectedIndex: number;
    onSelect: (username: string) => void;
}

const MentionDropdown: React.FC<MentionDropdownProps> = ({ results, selectedIndex, onSelect }) => {
    const listRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        // Scroll the selected item into view
        if (listRef.current) {
            const activeItem = listRef.current.children[selectedIndex] as HTMLElement;
            if (activeItem) {
                activeItem.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
            }
        }
    }, [selectedIndex]);

    if (results.length === 0) return null;

    return (
        <div 
            ref={listRef}
            className="absolute bottom-full mb-2 left-0 w-64 max-h-48 overflow-y-auto custom-scrollbar bg-surface dark:bg-slate-800 rounded-2xl shadow-[0_24px_60px_-24px_rgba(0,0,0,0.35)] border border-black/[0.06] dark:border-slate-700 py-2 z-[9999] animate-in slide-in-from-bottom-2 fade-in duration-200"
        >
            {results.map((profile, idx) => {
                const isActive = idx === selectedIndex;
                
                // Parse avatar config if it's a string
                const avatarConfig = typeof profile.avatar_config === 'string' 
                    ? JSON.parse(profile.avatar_config) 
                    : profile.avatar_config;

                return (
                    <button
                        key={profile.id}
                        onMouseDown={(e) => {
                            e.preventDefault(); // Prevents input from losing focus
                            onSelect(profile.username);
                        }}
                        className={`w-full flex items-center gap-3 px-4 py-2.5 transition-all text-left border-l-2 ${
                            isActive 
                                ? 'bg-primary/5 dark:bg-primary/10 border-primary' 
                                : 'border-transparent hover:bg-slate-50 dark:hover:bg-slate-700/50'
                        }`}
                    >
                        <div className="w-8 h-8 rounded-full bg-slate-100 dark:bg-slate-700 overflow-hidden flex-shrink-0 border border-black/[0.06] dark:border-slate-600">
                            {avatarConfig ? (
                                <AvatarRenderer config={avatarConfig} seed={profile.username} />
                            ) : profile.avatar_url ? (
                                <img src={profile.avatar_url} alt="" className="w-full h-full object-cover" />
                            ) : (
                                <div className="w-full h-full flex items-center justify-center text-[10px] font-normal text-slate-400 uppercase tracking-[0.14em] font-mono">
                                    {profile.username?.charAt(0) || '?'}
                                </div>
                            )}
                        </div>
                        <div className="flex flex-col min-w-0">
                            <span className="text-sm font-bold text-ink truncate flex items-center gap-1">
                                {profile.display_name}
                                {profile.plan_type === 'pro' && <Sparkles size={10} className="text-primary fill-primary flex-shrink-0" />}
                            </span>
                            <span className="text-[10px] font-mono text-slate-500 truncate">@{profile.username}</span>
                        </div>
                    </button>
                );
            })}
        </div>
    );
};

export default MentionDropdown;
